import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import CyclingPosturePage from "./pages/CyclingPosturePage";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { grey, blueGrey } from "@mui/material/colors";
import { createTheme, responsiveFontSizes } from "@mui/material";
import { Toaster } from "react-hot-toast";
import PageViewTracker from "./components/PageViewTracker";


/**
 * The root component of the application for Cycling Posture Analysis.
 *
 * @component
 * @returns {JSX.Element} The main app component.
 */
function App() {
  const [darkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });

  // const toggleDarkMode = () => {
  //   setDarkMode(!darkMode);
  // };

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
    // Sync DaisyUI theme with dark mode
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "cycling_dark" : "cycling"
    );
  }, [darkMode]);

  let theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: darkMode ? "#9061F0" : "#7133EA",
        contrastText: "#ffffff",
      },
      secondary: {
        main: darkMode ? "#2a2a3e" : "#F4F4F5",
        contrastText: darkMode ? "#e5e7eb" : "#374151",
      },
      background: {
        default: darkMode ? "#1a1a2e" : "#F4F4F4",
        paper: darkMode ? "#16213e" : "#ffffff",
      },
      text: {
        primary: darkMode ? grey[50] : "#374151",
        secondary: darkMode ? blueGrey[100] : "#6b7280",
      },
      success: {
        main: "#16a34a",
      },
      info: {
        main: darkMode ? "#3b82f6" : "#2563eb",
      },
      error: {
        main: "#EB504B",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    typography: {
      fontFamily: "Poppins, Lexend, Arial, sans-serif",
      h1: {
        fontSize: "3.125rem",
        fontWeight: 600,
        lineHeight: 1.2,
        xs: {
          fontSize: "4.125rem",
        },
        md: {
          fontSize: "5.125rem",
        },
      },
      h2: {
        fontSize: "2.625rem",
        fontWeight: 500,
        sm: {
          fontSize: "3.625rem",
        },
        md: {
          fontSize: "4.625rem",
        },
      },
      h3: {
        fontSize: "2.125rem",
        fontWeight: 500,
        lineHeight: 1.3,
        sm: {
          fontSize: "2.625rem",
        },
        md: {
          fontSize: "3.125rem",
        },
      },
      h4: {
        fontSize: "1.8125rem",
        fontWeight: 500,
        lineHeight: 1.4,
        sm: {
          fontSize: "2.125rem",
        },
        md: {
          fontSize: "2.625rem",
        },
      },
      h5: {
        fontSize: "1.5625rem",
        fontWeight: 500,
        lineHeight: 1.4,
        sm: {
          fontSize: "1.8125rem",
        },
        md: {
          fontSize: "2.125rem",
        },
      },
      h6: {
        fontSize: "1.3125rem",
        fontWeight: 500,
        lineHeight: 1.4,
        sm: {
          fontSize: "1.5625rem",
        },
        md: {
          fontSize: "1.8125rem",
        },
      },
      subtitle1: {
        fontSize: "1.15625rem",
        fontWeight: 500,
        lineHeight: 1.5,
        sm: {
          fontSize: "1.3125rem",
        },
        md: {
          fontSize: "1.5625rem",
        },
      },
      subtitle2: {
        fontSize: "1.03125rem",
        fontWeight: 500,
        lineHeight: 1.5,
        sm: {
          fontSize: "1.15625rem",
        },
        md: {
          fontSize: "1.3125rem",
        },
      },
      body1: {
        fontSize: "1.03125rem",
        lineHeight: 1.5,
        sm: {
          fontSize: "1.15625rem",
        },
        md: {
          fontSize: "1.3125rem",
        },
      },
      body2: {
        fontSize: "0.90625rem",
        lineHeight: 1.43,
        sm: {
          fontSize: "1.03125rem",
        },
      },
      button: {
        fontSize: "0.9875rem",
        fontWeight: 500,
        lineHeight: 1.75,
        textTransform: "uppercase",
      },
      caption: {
        fontSize: "0.78125rem",
        lineHeight: 1.66,
        fontWeight: 400,
      },
      overline: {
        fontSize: "0.78125rem",
        fontWeight: 400,
        lineHeight: 2.66,
        textTransform: "uppercase",
      },
    },
  });

  theme = responsiveFontSizes(theme);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster
          position="top-right"
          containerStyle={{
            top: 20,
          }}
        />
        <Router>
          <PageViewTracker />
          <div className="app-layout">
            <main className="app-main">
              <Routes>
                <Route path="/" element={<CyclingPosturePage />} />
              </Routes>
            </main>
          </div>
        </Router>
    </ThemeProvider>
  );
}

export default App;
